import { Dayjs } from "dayjs";

/**
 * Use this interface like
 * TimeFrame {
 *      start: Dayjs,
 *      end: Dayjs
 * }
 * => the other properties are only part of the interface for EKP
 * backwards compatibility
 */
export type TimeFrame = {
    interval?: TimeRange;
    start: Dayjs | Date;
    end: Dayjs | Date;
};

export enum TimeRange {
    FREE = "FREE",
    ONE_MINUTE = "ONE_MINUTE",
    FIVE_MINUTES = "FIVE_MINUTES",
    ONE_HOUR = "ONE_HOUR",
    THREE_HOURS = "THREE_HOURS",
    TWENTY_FOUR_HOURS = "TWENTY_FOUR_HOURS",
    YESTERDAY = "YESTERDAY",
    TWO_DAYS = "TWO_DAYS",
    SEVEN_DAYS = "SEVEN_DAYS",
    FOURTEEN_DAYS = "FOURTEEN_DAYS",
    THIRTY_DAYS = "THIRTY_DAYS",
    THREE_HUNDRED_65_DAYS = "THREE_HUNDRED_65_DAYS",
    /**
     * CUSTOM can be used to set defined timeframes by the onTimeFrameChange hook and not by the datepicker
     */
    CUSTOM = "CUSTOM",
    /**
     * DAY and TODAY refer to the same TimeRange,
     * one is used in the FPP, and one in the EKP
     */
    DAY = "DAY",
    TODAY = "TODAY",
    WEEK = "WEEK",
    MONTH = "MONTH",
    YEAR = "YEAR",
    YEARS = "YEARS",
}

export const isTimeRange = (s: string): s is TimeRange => (Object.values(TimeRange) as string[]).includes(s);

export enum DataSeriesUnit {
    WATT = "W",
    KILOWATT = "kW",
    WH = "Wh",
    KWH = "kWh",
    KVA = "kVA",
    VAH = "VAh",
    PERCENT = "%",
    VOLT = "V",
    AMPERE = "A",
    CELSIUS = "°C",
    TIME = " ",
    STATE = "",
}

export type ChartLegendItem = {
    /**
     * A unique identifier for the chart legend item.
     */
    id: string;

    /**
     * Translation is displayed as the name of the legend item.
     */
    translation: string;

    /**
     * The color that represents this legend item in the chart.
     * Should be a valid CSS color value.
     */
    color: string;

    /**
     * The numerical value that is associated with this legend item.
     * Should be the aggregated legend number.
     */
    legendValue?: number;

    /**
     * The unit of measurement for the data series of the associated legend item.
     */
    dataSeriesUnit?: DataSeriesUnit;

    /**
     * Skip the processing of the legend value (usually something like decimalisation, checking if a data unit exists, ...)
     */
    skipLegendValueProcessing?: boolean;
    /**
     * Label for tooltip
     */
    tooltipLabel?: string;
};

export type Week = {
    from: Date;
    to: Date;
};

export type AxisConfig = {
    unit: DataSeriesUnit;
    ticks?: string[] | undefined;
    maxValue?: number | undefined;
};
